import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Camera, Film } from 'lucide-react';
import { API_BASE } from '../config/api';

const API = API_BASE;

const ShotList = () => {
  const { filmId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [film, setFilm] = useState(null);
  const [shots, setShots] = useState([]);
  const [loading, setLoading] = useState(true);

  const filterStatus = searchParams.get('status'); // e.g., 'covered'

  useEffect(() => {
    fetchData();
  }, [filmId, filterStatus]);

  const fetchData = async () => {
    try {
      const [filmRes, shotsRes] = await Promise.all([
        axios.get(`${API}/films/${filmId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/films/${filmId}/shots${filterStatus ? `?status=${filterStatus}` : ''}`, { 
          headers: getAuthHeaders() 
        })
      ]);
      setFilm(filmRes.data);
      setShots(shotsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!film) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-destructive">Film not found</div>
      </div>
    );
  }

  const getTitle = () => {
    if (filterStatus === 'covered') return 'Covered Shots';
    if (filterStatus === 'planned') return 'Planned Shots';
    if (filterStatus === 'shot') return 'Shot (Not Covered)';
    if (filterStatus === 'pickup_needed') return 'Pickup Needed';
    if (filterStatus === 'dropped') return 'Dropped Shots';
    return 'All Shots';
  };

  return (
    <div className="bg-background" data-testid="shot-list">
      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex items-center gap-2 md:gap-4 mb-6 md:mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/films/${filmId}`)}
            data-testid="back-button"
            className="flex-shrink-0 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="text-xs font-semibold tracking-wider uppercase">Back</span>
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-bold font-display tracking-tight">
              {getTitle()} — {film.title}
            </h1>
            <p className="text-xs md:text-sm text-muted-foreground">
              {shots.length} shot{shots.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        {shots.length === 0 ? (
          <div className="text-center py-12">
            <Camera className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No shots found</h3>
            <p className="text-muted-foreground mb-6">
              {filterStatus 
                ? `No ${filterStatus} shots in this film yet.`
                : 'Create shots from the Scene Board'}
            </p>
            <Button onClick={() => navigate(`/films/${filmId}/board`)}>
              Open Scene Board
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {shots.map((shot) => (
              <Card
                key={shot.id}
                className="p-5 hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/scenes/${shot.scene_id}`)}
                data-testid={`shot-item-${shot.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Shot Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-mono font-semibold uppercase tracking-wider text-muted-foreground">
                        Scene {shot.scene_number}
                      </span>
                      <span className="text-sm font-mono font-semibold text-primary">
                        {shot.shot_code}
                      </span>
                      <ShotStatusBadge status={shot.status} />
                    </div>
                    {shot.description && (
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {shot.description}
                      </p>
                    )}
                  </div>

                  {/* Quick Action Indicator */}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Film className="h-4 w-4" />
                    <span>Click to view scene</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

const ShotStatusBadge = ({ status }) => {
  const colors = {
    planned: 'bg-secondary text-foreground',
    shot: 'bg-warning/15 text-warning',
    covered: 'bg-success/15 text-success',
    dropped: 'bg-destructive/15 text-destructive',
    pickup_needed: 'bg-warning/15 text-warning'
  };

  const labels = {
    planned: 'Planned',
    shot: 'Shot',
    covered: 'Covered',
    dropped: 'Dropped',
    pickup_needed: 'Pickup Needed'
  };

  return (
    <span className={`px-2 py-1 rounded-sm text-xs font-medium ${colors[status]}`}>
      {labels[status]}
    </span>
  );
};

export default ShotList;
